/**
 * Báo lỗi crash ẩn danh (M3, master-plan 2.4).
 *
 * - Chỉ gửi: stack trace, phiên bản game, trình duyệt, thời gian.
 * - KHÔNG có ID người dùng, KHÔNG dữ liệu hành vi, KHÔNG email/IP lưu trữ.
 * - Lỗi được xếp hàng vào localStorage khi xảy ra, gửi ở lần mở game sau
 *   (không làm gián đoạn lúc crash). Mặc định HỎI trước khi gửi
 *   (xem Chính sách riêng tư trong game).
 */

export interface BaoCaoCrash {
  stack: string;
  phienBan: string;
  trinhDuyet: string;
  duongDan: string;
  thoiGian: string;
}

const KHOA_HANG_DOI = 'idle-shop-crash-queue';
const KHOA_DONG_Y = 'idle-shop-crash-consent'; // 'hoi' | 'luon' | 'khong'

/** Phiên bản game hiển thị trong báo cáo (đồng bộ với package.json). */
export const PHIEN_BAN_GAME = '0.3.0-m3';

/** Dựng payload ẩn danh từ một Error. Không bao giờ gắn ID người dùng. */
export function dungBaoCaoCrash(
  loi: { stack?: string; message?: string },
  phienBan = PHIEN_BAN_GAME,
): BaoCaoCrash {
  const stack = (loi.stack ?? loi.message ?? String(loi)).slice(0, 8000);
  const trinhDuyet =
    typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 300) : 'unknown';
  const duongDan = typeof location !== 'undefined' ? location.pathname.slice(0, 200) : '/';
  return {
    stack,
    phienBan,
    trinhDuyet,
    duongDan,
    thoiGian: new Date().toISOString(),
  };
}

/** Xếp một báo cáo vào hàng đợi localStorage (gọi trong handler lỗi). */
export function xepHangBaoCao(baoCao: BaoCaoCrash): void {
  try {
    const raw = localStorage.getItem(KHOA_HANG_DOI);
    const hang: BaoCaoCrash[] = raw ? (JSON.parse(raw) as BaoCaoCrash[]) : [];
    hang.push(baoCao);
    // Giữ tối đa 5 báo cáo gần nhất.
    localStorage.setItem(KHOA_HANG_DOI, JSON.stringify(hang.slice(-5)));
  } catch {
    // localStorage đầy/khóa: bỏ qua, không crash thêm.
  }
}

/** Đọc hàng đợi (UI dùng để hỏi người chơi có gửi không). */
export function docHangDoi(): BaoCaoCrash[] {
  try {
    const raw = localStorage.getItem(KHOA_HANG_DOI);
    return raw ? (JSON.parse(raw) as BaoCaoCrash[]) : [];
  } catch {
    return [];
  }
}

/** Xóa hàng đợi sau khi đã xử lý. */
export function xoaHangDoi(): void {
  try {
    localStorage.removeItem(KHOA_HANG_DOI);
  } catch {
    // Bỏ qua.
  }
}

export type DongYCrash = 'hoi' | 'luon' | 'khong';

export function docDongY(): DongYCrash {
  try {
    const v = localStorage.getItem(KHOA_DONG_Y);
    if (v === 'luon' || v === 'khong') return v;
  } catch {
    // Bỏ qua.
  }
  return 'hoi';
}

export function datDongY(cheDo: DongYCrash): void {
  try {
    localStorage.setItem(KHOA_DONG_Y, cheDo);
  } catch {
    // Bỏ qua.
  }
}

/** Gửi một báo cáo tới Pages Function. Trả về true nếu gửi thành công. */
export async function guiBaoCao(baoCao: BaoCaoCrash): Promise<boolean> {
  try {
    const res = await fetch('/api/crash-report', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(baoCao),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * Cài handler bắt lỗi toàn cục. Gọi một lần khi khởi động app.
 * Chỉ xếp hàng — việc gửi do UI quyết định ở lần mở sau.
 */
export function caiDatBatLoi(): void {
  const xuLy = (loi: { stack?: string; message?: string }): void => {
    xepHangBaoCao(dungBaoCaoCrash(loi));
  };
  window.addEventListener('error', (e) => xuLy(e.error ?? { message: e.message }));
  window.addEventListener('unhandledrejection', (e) =>
    xuLy(e.reason as { stack?: string; message?: string }),
  );
}
